import { useRef } from "react"

import { Box, SvgIcon as MUISvgIcon, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"

import { gsap } from "@ggs/ui/local/gsap"
import type { FC, ResponsiveImageType } from ".."
import { Button, Container, IconButton, ResponsiveImage, SvgIcon, Tag, WYSIWYG } from ".."
import useIsomorphicLayoutEffect from "../../helpers/useIsomorphicLayoutEffect"

type FeaturedGuideProps = {
  title: string
  description: string
  tags?: string[]
  link?: {
    title: string
    uri: string
  }
  image: ResponsiveImageType
}

const FeaturedGuide: FC<FeaturedGuideProps> = ({ title, description, link, image }) => {
  return (
    <Container maxWidth={1520} position="relative" pt={20} pb={5}>
      <AnimatedSvgIcon />
      <FeaturedGuideImage {...{ image, link, title }} />
      <FeaturedGuideContent {...{ title, description, link }} />
    </Container>
  )
}
export default FeaturedGuide

/** A sub-component for the image in the featured guide component */
type ImageProps = Pick<FeaturedGuideProps, "image" | "link" | "title">
const FeaturedGuideImage: FC<ImageProps> = ({ image, link, title }) => {
  return (
    <Box ml={{ lg: 120 }} position="relative" right={{ xs: "auto", lg: 0 }}>
      <Box
        position="relative"
        height="100%"
        width="100%"
        maxWidth={1000}
        overflow="hidden"
        sx={(theme) => ({
          "borderRadius": { xs: theme.spacing(1.5, 1.5, 0, 0), lg: 1.5 },
          "aspectRatio": "10 / 7",
          "zIndex": 0,
          "&:hover": {
            ".arrow-icon": {
              bottom: 20,
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        })}
      >
        <ResponsiveImage
          image={image}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
          }}
        />
        {link && (
          <IconButton
            className="arrow-icon"
            href={link.uri}
            aria-label={`${link.title} about ${title}`}
            variant="base"
            icon={<SvgIcon variant="arrowOutward" size={{ xs: 12, md: 16 }} />}
            sx={(theme) => ({
              bgcolor: theme.palette["neutral.200"].main,
              position: "absolute",
              transition: theme.transitions.create(["opacity", "bottom", "transform"]),
              opacity: 0,
              transform: "translateY(5px)",
              bottom: { xs: theme.spacing(5), lg: theme.spacing(-20) },
              right: { xs: theme.spacing(5), lg: theme.spacing(10) },
            })}
          />
        )}
      </Box>
    </Box>
  )
}

/** A sub-component for the content in the featured guide component */
type ContentProps = Pick<FeaturedGuideProps, "title" | "description" | "link">
const FeaturedGuideContent: FC<ContentProps> = ({ title, description, link }) => {
  return (
    <Stack
      width={{ lg: 582 }}
      bgcolor={(theme) => theme.palette["secondary.300"].main}
      boxShadow={(theme) => `0 0 8px 0 ${alpha(theme.palette["secondary.800"].main, 0.1)}`}
      color={(theme) => theme.palette["secondary.800"].main}
      mt={{ lg: -126 }}
      position="relative"
      zIndex={1}
      sx={(theme) => ({
        borderRadius: { xs: theme.spacing(0, 0, 1.5, 1.5), lg: 1.5 },
      })}
    >
      <Box
        py={{ xs: 3.5, lg: 4.5 }}
        px={{ xs: 5, lg: 10 }}
        borderBottom={(theme) => `1px solid ${alpha(theme.palette["secondary.800"].main, 0.1)}`}
      >
        <Tag label="Featured" color="blue" />
      </Box>
      <Box px={{ xs: 5, lg: 10 }} pt={{ xs: 10, lg: 30 }} pb={{ xs: 5, lg: 20 }}>
        <Typography component="h2" variant="heading.xl" mb={{ xs: 3, lg: 5 }}>
          {title}
        </Typography>
        <WYSIWYG content={description} />
        {link && (
          <Box mt={10}>
            <Button
              variant="neutral.secondary"
              href={link.uri}
              aria-label={`${link.title} about ${title}`}
              sx={{
                display: { xs: "flex", md: "inline-flex" },
                textAlign: { xs: "center", mdx: "left" },
                width: { xs: "100%", md: "auto" },
              }}
            >
              {link.title}
            </Button>
          </Box>
        )}
      </Box>
    </Stack>
  )
}

/** A sub-component for the line in the featured guide component */
const AnimatedSvgIcon = () => {
  const mainRef = useRef<HTMLDivElement>(null)
  const pathRef = useRef<SVGPathElement>(null)
  const pointRef = useRef<SVGGElement>(null)

  useIsomorphicLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(pathRef.current, {
        strokeDashoffset: 849,
        scrollTrigger: {
          trigger: mainRef.current,
          start: "top 75%",
          end: "bottom center",
          scrub: true,
          // markers: true,
        },
      })
      gsap.to(pointRef.current, {
        opacity: 1,
        scrollTrigger: {
          trigger: mainRef.current,
          start: "bottom center",
          end: "+=100",
          scrub: true,
          // markers: true,
        },
      })
    }, mainRef)
    return () => ctx.revert()
  }, [mainRef, pathRef, pointRef])

  return (
    <Box
      ref={mainRef}
      sx={(theme) => ({
        width: 829,
        height: 207,
        position: "absolute",
        top: 196,
        left: -160,
        display: { xs: "none", lg: "block" },
        zIndex: 0,
      })}
    >
      <MUISvgIcon
        viewBox="0 0 829 207"
        sx={{
          width: 829,
          height: 207,
        }}
      >
        <path
          d="M828.292 45.367c-43.606-15.955-86.898-39.872-133.39-42.992C631.215-1.9 585.097 27.83 542.544 60.646c-30.253 23.33-62.846 44.691-94.447 66.708-40.773 28.409-81.521 56.592-134.048 69.847-52.768 13.315-111.636 9.665-168.236 3.141-32.471-3.742-70.003-11.736-101.17-23.139"
          fill="none"
          stroke="#F05B2A"
          strokeWidth="2"
          strokeDasharray=".1 10"
          strokeLinecap="round"
        />
        <path
          ref={pathRef}
          d="M828.292 45.367c-43.606-15.955-86.898-39.872-133.39-42.992C631.215-1.9 585.097 27.83 542.544 60.646c-30.253 23.33-62.846 44.691-94.447 66.708-40.773 28.409-81.521 56.592-134.048 69.847-52.768 13.315-111.636 9.665-168.236 3.141-32.471-3.742-70.003-11.736-101.17-23.139"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="3"
          strokeDasharray={849}
          strokeDashoffset={849 * 2}
        />
        <g ref={pointRef} opacity="0">
          <path
            d="M46.732 173.563c-4.414 12.498-18.123 19.053-30.622 14.64-12.498-4.414-19.053-18.123-14.64-30.622 4.413-12.498 18.123-19.053 30.622-14.64 12.498 4.413 19.053 18.123 14.64 30.622Z"
            fill="#FDEEE9"
            fillOpacity="0.4"
          />
          <path d="M28.673 167.386a4 4 0 1 1-7.544-2.665 4 4 0 0 1 7.544 2.665Z" fill="#F05B2A" />
        </g>
      </MUISvgIcon>
    </Box>
  )
}
